import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { AlertTriangle, Trash2, X } from "lucide-react";
import type { CleanupJobView, PocTenantView } from "@ubisafe/shared";
import { api, ApiClientError } from "../lib/api";

interface CleanupConfirmationModalProps {
  tenant: PocTenantView;
  onClose: () => void;
  onStarted: (job: CleanupJobView) => void;
}

const affectedEntities = [
  "Agendas, horários e configurações das contas",
  "Intrusões, partições, PGMs e eventos periódicos",
  "Dispositivos e centrais",
  "Vínculos de contatos",
  "Contas e patrimônios",
  "Contatos e clientes",
];

export function CleanupConfirmationModal({ tenant, onClose, onStarted }: CleanupConfirmationModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const matches = confirmation === tenant.identifier;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!matches || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      onStarted(await api.createCleanupJob(tenant.identifier, confirmation));
    } catch (requestError) {
      setError(requestError instanceof ApiClientError ? requestError.message : "Não foi possível iniciar a limpeza.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !submitting) onClose();
    }}>
      <section
        className="modal-card cleanup-confirmation-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <header className="modal-card__header">
          <div className="modal-card__title">
            <span className="modal-card__icon cleanup-confirmation-modal__icon"><AlertTriangle aria-hidden="true" /></span>
            <div>
              <span className="eyebrow eyebrow--dark">Confirmação obrigatória</span>
              <h2 id={titleId}>Limpar tenant — exclusão real</h2>
            </div>
          </div>
          <button className="icon-button" type="button" onClick={onClose} disabled={submitting} aria-label="Fechar confirmação">
            <X aria-hidden="true" />
          </button>
        </header>

        <form onSubmit={submit} className="cleanup-confirmation-form">
          <div className="cleanup-impact" id={descriptionId}>
            <Trash2 aria-hidden="true" />
            <div>
              <strong>{tenant.name}</strong>
              <span>Identificador {tenant.identifier}</span>
            </div>
          </div>

          <div className="inline-alert inline-alert--warning cleanup-simulation-note" role="note">
            <AlertTriangle aria-hidden="true" />
            <div>
              <strong>Esta ação criará um plano persistente de exclusão real.</strong>
              <span>Após o inventário, cada grupo exibirá sua quantidade e somente será executado depois de uma confirmação específica. A POC será encerrada automaticamente apenas se as verificações confirmarem zero resíduos.</span>
            </div>
          </div>

          <div className="cleanup-entity-list" aria-label="Grupos contemplados pela limpeza">
            <span>Grupos contemplados, sem feriados nem automações globais de OS</span>
            <ul>{affectedEntities.map((entity) => <li key={entity}>{entity}</li>)}</ul>
          </div>

          <label className="form-field cleanup-confirmation-field">
            <span>Digite <strong>{tenant.identifier}</strong> para confirmar</span>
            <input
              ref={inputRef}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={Boolean(confirmation) && !matches}
              placeholder={tenant.identifier}
            />
            {confirmation && !matches && <small className="field-error">O Identificador deve ser digitado exatamente.</small>}
          </label>

          {error && <div className="inline-alert inline-alert--error" role="alert">{error}</div>}

          <footer className="modal-actions">
            <button className="secondary-button" type="button" onClick={onClose} disabled={submitting}>Cancelar</button>
            <button className="danger-button" type="submit" disabled={!matches || submitting}>
              {submitting ? "Criando plano…" : "Criar plano de limpeza"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}
